<template>
  <div class="event">
    <div class="eventRow">
      <!-- ČAS -->
      <div class="eventTime">
        <span>{{ event.timeFrom | time }}</span>
        <span v-if="event.timeTo" class="dash">–</span>
        <span v-if="event.timeTo">{{ event.timeTo | time }}</span>
      </div>

      <!-- INTERPRET -->
      <div class="eventTitle">
        <span v-for="(interpret, idx) in (event.interprets || [])" :key="interpret.id">
          <a class="interpretLink" :href="'/program/interpret/' + interpret.id">
            {{ interpret.name | translation }}
          </a>
          <span v-if="idx < event.interprets.length - 1" class="amp">&nbsp;&amp;&nbsp;</span>
        </span>
      </div>
    </div>

    <!-- META: ŽÁNRY + VSTUPENKY -->
    <div v-if="showMeta" class="eventMeta">

      <!-- ŽÁNRY -->
      <div class="genres">
        <span
          v-for="g in (metaGenres || [])"
          :key="g.id || (g.name && g.name.cs) || JSON.stringify(g)"
          class="genre"
        >
          <span class="genreDot" :style="{ backgroundColor: genreColor(g) }"></span>
          <span class="genreName">{{ g.name | translation }}</span>
        </span>
      </div>

      <!-- TICKET -->
      <div v-if="metaTicketLink" class="ticket">
        <span class="arrow">→</span>
        <a
          v-if="metaTicketLink !== 'free'"
          class="ticketLink"
          :href="metaTicketLink"
        >
          {{ "Navigation.entrance.tickets" | staticTranslation }}
        </a>
        <span v-else class="ticketLink ticketFree">
          {{ "Navigation.entrance.free" | staticTranslation }}
        </span>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: "ProgramEventItem",
  props: {
    event: { type: Object, required: true },
    showMeta: { type: Boolean, default: false },
    metaGenres: { type: Array, default: () => [] },
    metaTicketLink: { type: [String, null], default: null }
  },
  methods: {
    genreColor(g) {
      const c = g && g.color;
      return (typeof c === "string" && c.trim().length > 0) ? c : "#000";
    }
  }
};
</script>

<style scoped>
/* =====================================
   ZÁKLAD
   ===================================== */

.event {
  padding: 12px 0 0 0;
}

.eventRow {
  display: flex;
  align-items: baseline;
  gap: 18px;
}

/* =====================================
   ČAS – Mono Regular 20 (desktop)
   ===================================== */

.eventTime {
  min-width: 155px;
  font-family: "Transcript Mono";
  font-weight: 400;
  font-size: 20px;
  line-height: 1;
  color: #000;
}

.dash { padding: 0; }

/* =====================================
   INTERPRET – Mono Bold 24
   ===================================== */

.eventTitle {
  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 24px;
  line-height: 1.2;
  color: #000;
}

.interpretLink {
  color: #000;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 5px;
  border: none;
  padding: 0;
}

.interpretLink:hover {
  text-decoration: none;
  background: transparent;
  color: #000;
}

.amp { font-weight: 700; }

/* =====================================
   META BLOK (DESKTOP)
   ===================================== */

.eventMeta {
  display: inline-flex;
  align-items: center;
  margin-top: 13px;
}

/* ŽÁNRY */
.genres {
  display: inline-flex;
  align-items: center;
}

/* Mezera pouze mezi žánry */
.genre {
  display: inline-flex;
  align-items: center;
  margin-right: 12px;

  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 17px;
  line-height: 1.1;
  color: #000;
  position: relative;
  top: 2px;
}

.genreDot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  margin-right: 8px;
  position: relative;
  top: -2.5px;
}

/* TICKET BLOK – bez vlastní mezery */
.ticket {
  display: inline-flex;
  align-items: center;
}

/* =====================================
   ŠIPKA – SYMETRICKÁ MEZERA
   ===================================== */

.arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  margin: 0 16px;   /* ✅ stejná mezera před i za */

  font-size: 17px;
  line-height: 1;
}

/* ODKAZ NA VSTUPENKY */

.ticketLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #000;
  padding: 6px 12px;
  line-height: 1;

  font-family: "Transcript Pro Mono Regular";
  font-weight: 700;
  font-size: 17px;

  color: #000;
  text-decoration: none;
  background: transparent;
}

.ticketLink:hover {
  background: #000;
  color: #FFCE6D;
}

.ticketFree { cursor: default; }

/* =====================================
   MOBILE
   ===================================== */
@media (max-width: 768px) {

  .genreDot {
    width: 10px;
    height: 10px;
  }

  .event {
    padding: 16px 0 0 0;
  }

  .eventRow {
    flex-direction: column;
    gap: 0;
  }

  .eventTime {
    min-width: 0;
    font-size: 17px;
  }

  .eventTitle {
    font-size: 20px;
  }

  .eventMeta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
    margin-top: 22px;
  }

  /* Žánry – bez vlastního margin-bottom */
  .genre {
    font-size: 14px;
    margin-right: 10px;
    margin-bottom: 0;   /* důležité */
    top: 0;
  }

  .genres {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;   /* vertikální i horizontální mezera mezi žánry */
  }

  .ticket {
    font-size: 14px;
    margin: 0;       /* žádné vlastní odsazení */
  }

  .ticketLink {
    font-size: 14px;
    padding: 6px 10px;
  }

  /* Na mobilu šipku skrýt */
  .arrow {
    display: none;
  }
}

</style>
